// TuitionBoard.js
import React, { useEffect, useMemo, useState, useContext } from "react";
import "../styles/TuitionBoard.css";
import Select from "react-select";
import { Filter } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  BadgeCheck,
  UserPlus,
  Activity,
  Users,
  Building,
  Sparkles,
  Phone,
  MessageCircle,
  MapPin,
  CalendarDays,
  Banknote,
} from "lucide-react";
import Footer from "../Footer";
import { UserContext } from "../context/UserContext";
// import usePersistScreenshotBlackout from "../../hooks/usePersistScreenshotBlackout";

const API_BASE = "https://tn.server.goatteachers.com/api";

const PAGE_SIZE = 10;

// ===== WhatsApp helpers (BD phone normalization + message) =====
const normalizeBDPhone = (raw) => {
  if (!raw) return "";
  let d = String(raw).replace(/\D/g, ""); // digits only

  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("880")) return d; // already E.164 for BD
  if (d.startsWith("88")) return d; // 88 + local
  if (d.startsWith("0")) return "880" + d.slice(1); // 017.. -> 88017..
  if (d.startsWith("1")) return "880" + d; // 17.. -> 88017..
  return d;
};

const buildTutorWhatsappText = ({ tutor, tutorProfileUrl, tuitionCode }) => {
  return `আসসালামু আলাইকুম,

Tutor Network থেকে আমাকে ${tuitionCode || "-"} শিক্ষার্থীর টিউশনের জন্য Assign করা হয়েছে।

আমি এই টিউশনের জন্য আগ্রহী। আমার তথ্য নিচে দেওয়া হলো:

নাম: ${tutor?.name || "-"}
শিক্ষাগত যোগ্যতা:
${tutor?.degree || "-"}, ${tutor?.field_of_study || "-"}, ${tutor?.institute || "-"}

Tutor Profile:
${tutorProfileUrl || "-"}

আশা করি শিক্ষার্থীর প্রয়োজন অনুযায়ী ভালোভাবে সহযোগিতা করতে পারব।

ধন্যবাদ।
${tutor?.name || "-"}`;
};

// Shuffle helper so posts appear in random order each load
function shuffleArray(array) {
  const arr = [...array]; // avoid mutating original
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// ---- Counter Section (tnc-) ----
const TNCounterSection = React.memo(function TNCounterSection() {
  const formatBn = (n) => {
    try {
      return new Intl.NumberFormat("bn-BD").format(n);
    } catch {
      const map = ["০", "১", "২", "৩", "৪", "৫", "৬", "৭", "৮", "৯"];
      return String(n).replace(/\d/g, (d) => map[d]);
    }
  };

  const targets = React.useMemo(() => {
    const r = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
    return {
      liveTutors: r(600, 1000),
      positions: r(600, 1000),
      institutes: 64,
      newTutors: r(54, 100),
    };
  }, []);

  const useCountUp = (to, duration = 1800) => {
    const [val, setVal] = React.useState(0);
    React.useEffect(() => {
      let raf = 0,
        start = 0;
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min(1, (ts - start) / duration);
        setVal(Math.floor(p * to));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
      return () => cancelAnimationFrame(raf);
    }, [to, duration]);
    return val;
  };

  const v1 = useCountUp(targets.liveTutors);
  const v2 = useCountUp(targets.positions);
  const v3 = useCountUp(targets.institutes);
  const v4 = useCountUp(targets.newTutors);

  const items = [
    { icon: Activity, label: "লাইভ টিউশন", value: v1 },
    { icon: Users, label: "লাইভ টিউটর", value: v2, plus: true },
    { icon: Building, label: "নতুন টিউশন", value: v3 },
    { icon: Sparkles, label: "ভেরিফাইড টিউটর", value: v4 },
  ];

  return (
    <section className="tnc-section" aria-label="site counters">
      <div className="tnc-wrap">
        {items.map(({ icon: Icon, label, value, plus }, i) => (
          <div className="tnc-card" key={i}>
            <div className="tnc-iconRing">
              <span className="tnc-icon">
                <Icon size={22} aria-hidden="true" />
              </span>
              <span className="tnc-pulse" aria-hidden="true" />
            </div>
            <div className="tnc-texts baloo-da-2-tutor">
              <div className="tnc-label">{label}</div>
              <div className="tnc-value">
                {formatBn(value)}
                {plus && value >= targets.positions ? "+" : ""}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
});

// Safely extract texts from a post coming from /tuition-board
function mapPost(raw, index) {
  const name = raw.student_name || "Unnamed Student";
  const medium = raw.StudentMedium?.name || "";
  const level = raw.StudentLevel?.name || "";

  const subjects =
    raw.TuitionPostSubjects?.map((ts) => ts.StudentSubject?.name).filter(
      Boolean,
    ) || [];

  const areas =
    raw.TuitionPostAreas?.map((ta) => ta.Area?.name).filter(Boolean) || [];

  const division = raw.Division?.name || "";
  const district = raw.District?.name || "";

  const locationParts = [
    areas.join(", "),
    district || "",
    division || "",
  ].filter(Boolean);

  const location =
    locationParts.length > 0 ? locationParts.join(" • ") : "Location not set";

  // poster type, salary, institute, weekdays
  const posterType = raw.poster_type || "Student";

  const minSalary = raw.min_salary ?? null;
  const maxSalary = raw.max_salary ?? null;

  let salaryText = "";
  if (minSalary && maxSalary && minSalary !== maxSalary) {
    salaryText = `${minSalary} - ${maxSalary} ৳`;
  } else if (minSalary && !maxSalary) {
    salaryText = `${minSalary} ৳ বা বেশি`;
  } else if (!minSalary && maxSalary) {
    salaryText = `সর্বোচ্চ ${maxSalary} ৳`;
  } else if (minSalary && maxSalary && minSalary === maxSalary) {
    salaryText = `${minSalary} ৳`;
  }

  const institutePreference = raw.InstitutePreference?.name || "Any";

  const weekdays = raw.Weekdays?.map((w) => w.name).filter(Boolean) || [];

  return {
    id: raw.id,
    name,
    medium,
    level,
    subjects,
    location,
    genderPreference: raw.gender_preference || "",
    phone: raw.phone_number || "",
    whatsapp: raw.whatsapp_number || "",
    posterType,
    salaryText,
    institutePreference,
    weekdays,

    raw,
  };
}

export default function TuitionBoard() {
  const [tutorData, setTutorData] = useState(null);
  // usePersistScreenshotBlackout({ enabled: true });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [blurred, setBlurred] = useState(false);

  const [filters, setFilters] = useState({
    search: "",
    medium: "all",
    level: "all",
    gender: "all",
    division: "all",
    district: "all",
    area: "all",
    subject: "all",
  });

  const [showFilters, setShowFilters] = useState(false);

  /* =========================
     Credits / Wallet UI state
     ========================= */

  const [wallet, setWallet] = useState({
    credits: 0,
    free_unlock_used: false,
    pending_topups: 0,
  });

  const [packs, setPacks] = useState([]);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [selectedPackId, setSelectedPackId] = useState(null);
  const [senderNumber, setSenderNumber] = useState("");
  const [creditModalMsg, setCreditModalMsg] = useState("");
  const [creditLoading, setCreditLoading] = useState(false);

  // which post we tried to unlock (so user can retry after buying)
  const [pendingUnlockPostId, setPendingUnlockPostId] = useState(null);

  // same pattern as TutorProfile
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  // per-post contact visibility (one click shows both phone & WhatsApp)
  const [visibleContacts, setVisibleContacts] = useState({});
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [profileReminderPost, setProfileReminderPost] = useState(null);
  const [profileReminderShown, setProfileReminderShown] = useState({});

  const closeAgreement = () => {
    setAgreementOpen(false);
  };
  const [agreementPost, setAgreementPost] = useState(null);
  const [agreementLoading, setAgreementLoading] = useState(false);

  const isLoggedIn = !!user;
  const isTutor = user?.role === "tutor";
  const isStudent = user?.role === "student";
  const tutorId = tutorData?.id || tutorData?._id;
  const tutorProfileUrl = tutorId
    ? `${window.location.origin}/tutor/${tutorId}`
    : "";

  const whatsappText = useMemo(() => {
    return buildTutorWhatsappText({ tutor: tutorData, tutorProfileUrl });
  }, [tutorData, tutorProfileUrl]);

  const openTutorWhatsappProfile = ({ post }) => {
    const waPhone = normalizeBDPhone(post.whatsapp);

    if (!waPhone) return;

    const postWhatsappText = buildTutorWhatsappText({
      tutor: tutorData,
      tutorProfileUrl,
      tuitionCode: post.name,
    });

    window.open(
      `https://api.whatsapp.com/send?phone=${waPhone}&text=${encodeURIComponent(
        postWhatsappText,
      )}`,
      "_blank",
    );
  };

  // helper functions: fetch wallet/packs + submit “I Paid”
  const authHeaders = () => {
    const token = user?.token || localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchTutorProfile = async () => {
    if (!isTutor) return;
    try {
      const res = await fetch(`${API_BASE}/tutors/me`, {
        headers: { ...authHeaders() },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setTutorData(data?.tutor || null);
    } catch (e) {
      console.error("fetchTutorProfile error", e);
    }
  };

  const fetchWallet = async () => {
    if (!isTutor) return;
    try {
      const res = await fetch(`${API_BASE}/tutors/wallet/me`, {
        headers: { ...authHeaders() },
      });
      const data = await res.json();
      if (res.ok) {
        setWallet({
          credits: Number(data.credits || 0),
          free_unlock_used: !!data.free_unlock_used,
          pending_topups: Number(data.pending_topups || 0),
        });
      }
    } catch (e) {
      console.error("fetchWallet error", e);
    }
  };

  const fetchPacks = async () => {
    try {
      const res = await fetch(`${API_BASE}/tutors/wallet/packs`);
      const data = await res.json();
      if (res.ok) setPacks(Array.isArray(data.packs) ? data.packs : []);
    } catch (e) {
      console.error("fetchPacks error", e);
    }
  };

  const openCreditModal = async (postIdToUnlock = null) => {
    setCreditModalMsg("");
    setSenderNumber("");
    setSelectedPackId(null);
    setPendingUnlockPostId(postIdToUnlock);
    setShowCreditModal(true);
    setTopupInstructions(null);
    await fetchPacks();
    if (isTutor) await fetchWallet();
  };
  const [topupInstructions, setTopupInstructions] = useState(null);

  const submitTopupRequest = async () => {
    if (!selectedPackId) {
      setCreditModalMsg("Please select a pack.");
      return;
    }
    if (!isTutor) {
      setCreditModalMsg("Only tutors can purchase package.");
      return;
    }

    setCreditLoading(true);
    setCreditModalMsg("");

    try {
      const res = await fetch(`${API_BASE}/tutors/wallet/topup-request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({
          pack_id: selectedPackId,
          sender_number: senderNumber || null,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setCreditModalMsg(data?.message || "Failed to submit request.");
        return;
      }

      setTopupInstructions(data?.instructions || null);

      setCreditModalMsg(
        ` Request submitted! Please pay to ${
          data?.instructions?.pay_to || "the bKash number"
        } and wait for admin approval.`,
      );

      await fetchWallet();
    } catch (e) {
      console.error("submitTopupRequest error", e);
      setCreditModalMsg("Server error submitting request.");
    } finally {
      setCreditLoading(false);
    }
  };

  // Contact reveal helper function (now monetized)
  const revealPostContact = async (postId, method) => {
    try {
      const token = user?.token || localStorage.getItem("token");
      if (!token) return { ok: false, error: "No token" };

      const res = await fetch(
        `${API_BASE}/tutors/tuition-posts/${postId}/reveal`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ method }),
        },
      );

      const data = await res.json().catch(() => ({}));

      // If backend says purchase needed
      if (data?.requiresPurchase) {
        await fetchWallet();
        localStorage.setItem("pending_unlock_post", String(postId));
        navigate("/packages");
        window.scrollTo(0, 0);
        return { ok: false, requiresPurchase: true };
      }

      if (!res.ok || data?.success === false) {
        return { ok: false, error: data?.message || "Unlock failed" };
      }

      // success -> update wallet (credit could have been spent)
      await fetchWallet();

      // backend returns BOTH contact items after unlock (we’ll store them)
      return { ok: true, data };
    } catch (e) {
      console.error("reveal post contact failed", e);
      return { ok: false, error: "Server error" };
    }
  };

  const openTutorProfileMessage = (post) => {
    const waPhone = normalizeBDPhone(post.whatsapp);

    if (!waPhone) return;

    const postWhatsappText = buildTutorWhatsappText({
      tutor: tutorData,
      tutorProfileUrl,
      tuitionCode: post.name,
    });

    window.open(
      `https://api.whatsapp.com/send?phone=${waPhone}&text=${encodeURIComponent(
        postWhatsappText,
      )}`,
      "_blank",
    );
  };

  const handleSendProfile = (post) => {
    const alreadyShown = profileReminderShown[post.id];

    if (!alreadyShown) {
      setProfileReminderShown((prev) => ({
        ...prev,
        [post.id]: true,
      }));

      setProfileReminderPost(post);
      return;
    }

    openTutorProfileMessage(post);
  };

  // SCREEN SHOT PROTECTION BLUR
  // useEffect(() => {
  //   const onBlur = () => setBlurred(true);
  //   const onFocus = () => setBlurred(false);

  //   window.addEventListener("blur", onBlur);
  //   window.addEventListener("focus", onFocus);

  //   // optional: if user switches tab (some browsers)
  //   const onVis = () => setBlurred(document.hidden);
  //   document.addEventListener("visibilitychange", onVis);

  //   return () => {
  //     window.removeEventListener("blur", onBlur);
  //     window.removeEventListener("focus", onFocus);
  //     document.removeEventListener("visibilitychange", onVis);
  //   };
  // }, []);

  // scroll to top
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  // Fetch tuition posts
  useEffect(() => {
    let isMounted = true;

    async function fetchBoard() {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_BASE}/students/tuition-board?limit=500`);
        const data = await res.json();

        if (!res.ok) {
          throw new Error(
            data?.message || "টিউশন বোর্ড লোড করা যাচ্ছে না। পরে চেষ্টা করুন।",
          );
        }

        if (!isMounted) return;

        const mapped = (data.data || []).map((p, idx) => mapPost(p, idx));
        const shuffled = shuffleArray(mapped);
        setPosts(shuffled);
      } catch (err) {
        console.error("Fetch tuition board error:", err);
        if (isMounted) {
          setError(
            err.message ||
              "সার্ভার সমস্যার কারণে টিউশন বোর্ড লোড করা সম্ভব হয়নি।",
          );
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchBoard();
    return () => {
      isMounted = false;
    };
  }, []);

  // Load tutor wallet when tutor is logged in
  useEffect(() => {
    if (!isTutor) return;
    fetchWallet();
    fetchTutorProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTutor]);

  // Build filter options from loaded posts
  const mediumOptions = useMemo(
    () =>
      Array.from(new Set(posts.map((p) => p.medium).filter(Boolean))).sort(),
    [posts],
  );

  const levelOptions = useMemo(
    () => Array.from(new Set(posts.map((p) => p.level).filter(Boolean))).sort(),
    [posts],
  );

  const genderOptions = useMemo(
    () =>
      Array.from(
        new Set(posts.map((p) => p.genderPreference).filter(Boolean)),
      ).sort(),
    [posts],
  );

  const divisionOptions = useMemo(
    () =>
      Array.from(
        new Set(
          posts
            .map((p) => p.raw.Division?.name)
            .filter((v) => v && v.trim() !== ""),
        ),
      ).sort(),
    [posts],
  );

  const districtOptions = useMemo(
    () =>
      Array.from(
        new Set(
          posts
            .map((p) => p.raw.District?.name)
            .filter((v) => v && v.trim() !== ""),
        ),
      ).sort(),
    [posts],
  );

  const areaOptions = useMemo(
    () =>
      Array.from(
        new Set(
          posts
            .flatMap(
              (p) => p.raw.TuitionPostAreas?.map((ta) => ta.Area?.name) || [],
            )
            .filter((v) => v && v.trim() !== ""),
        ),
      ).sort(),
    [posts],
  );

  const subjectOptions = useMemo(
    () =>
      Array.from(
        new Set(
          posts.flatMap((p) => p.subjects).filter((v) => v && v.trim() !== ""),
        ),
      ).sort(),
    [posts],
  );

  // react-select options (with "Any" / "all")
  const mediumSelectOptions = useMemo(
    () => [
      { value: "all", label: "Any" },
      ...mediumOptions.map((m) => ({ value: m, label: m })),
    ],
    [mediumOptions],
  );

  const levelSelectOptions = useMemo(
    () => [
      { value: "all", label: "Any" },
      ...levelOptions.map((l) => ({ value: l, label: l })),
    ],
    [levelOptions],
  );

  const genderSelectOptions = useMemo(
    () => [
      { value: "all", label: "Any" },
      ...genderOptions.map((g) => ({ value: g, label: g })),
    ],
    [genderOptions],
  );

  const divisionSelectOptions = useMemo(
    () => [
      { value: "all", label: "Any" },
      ...divisionOptions.map((d) => ({ value: d, label: d })),
    ],
    [divisionOptions],
  );

  const districtSelectOptions = useMemo(
    () => [
      { value: "all", label: "Any" },
      ...districtOptions.map((d) => ({ value: d, label: d })),
    ],
    [districtOptions],
  );

  const areaSelectOptions = useMemo(
    () => [
      { value: "all", label: "Any" },
      ...areaOptions.map((a) => ({ value: a, label: a })),
    ],
    [areaOptions],
  );

  const subjectSelectOptions = useMemo(
    () => [
      { value: "all", label: "Any" },
      ...subjectOptions.map((s) => ({ value: s, label: s })),
    ],
    [subjectOptions],
  );

  // Apply filters + search
  const filteredPosts = useMemo(() => {
    return posts.filter((p) => {
      const {
        search,
        medium,
        level,
        gender,
        division,
        district,
        area,
        subject,
      } = filters;

      if (medium !== "all" && p.medium !== medium) return false;
      if (level !== "all" && p.level !== level) return false;
      if (gender !== "all" && p.genderPreference !== gender) return false;

      if (
        division !== "all" &&
        p.raw.Division?.name &&
        p.raw.Division.name !== division
      )
        return false;

      if (
        district !== "all" &&
        p.raw.District?.name &&
        p.raw.District.name !== district
      )
        return false;

      if (area !== "all") {
        const areaNames =
          p.raw.TuitionPostAreas?.map((ta) => ta.Area?.name).filter(Boolean) ||
          [];
        if (!areaNames.includes(area)) return false;
      }

      if (subject !== "all" && !p.subjects.includes(subject)) return false;

      if (search.trim()) {
        const s = search.toLowerCase();
        const haystack = [
          p.name,
          p.level,
          p.medium,
          p.location,
          p.subjects.join(" "),
          p.posterType,
          p.institutePreference,
          (p.weekdays || []).join(" "),
        ]
          .join(" ")
          .toLowerCase();

        if (!haystack.includes(s)) return false;
      }

      return true;
    });
  }, [posts, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / PAGE_SIZE));

  const pagePosts = useMemo(() => {
    const clampedPage = Math.min(page, totalPages);
    const start = (clampedPage - 1) * PAGE_SIZE;
    return filteredPosts.slice(start, start + PAGE_SIZE);
  }, [filteredPosts, page, totalPages]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPage(1);
  };

  const handleSelectChange = (key) => (option) => {
    handleFilterChange(key, option?.value || "all");
  };

  const handleResetFilters = () => {
    setFilters({
      search: "",
      medium: "all",
      level: "all",
      gender: "all",
      division: "all",
      district: "all",
      area: "all",
      subject: "all",
    });
    setPage(1);
  };

  const activeFilterCount = useMemo(() => {
    const { search, ...rest } = filters;
    let count = 0;
    if (search.trim()) count++;
    Object.values(rest).forEach((v) => {
      if (v !== "all") count++;
    });
    return count;
  }, [filters]);

  const hasActiveFilters = activeFilterCount > 0;

  const clearFilters = () => {
    handleResetFilters();
  };

  const openApplyAgreement = (post) => {
    if (!post?.id) return;

    setAgreementPost(post);
    setAgreementOpen(true);
  };

  const closeApplyAgreement = () => {
    if (agreementLoading) return;

    setAgreementOpen(false);
    setAgreementPost(null);
  };

  const continueApplyAfterAgreement = async () => {
    const post = agreementPost;

    if (!post?.id) return;

    setAgreementLoading(true);

    try {
      const result = await revealPostContact(post.id, "phone");

      if (result?.requiresPurchase) {
        setAgreementOpen(false);
        setAgreementPost(null);
        return;
      }

      if (!result?.ok) {
        alert(result?.error || "Failed to unlock contact.");
        return;
      }

      const contact = result?.data?.contact || {
        phone: result?.data?.phone_number,
        whatsapp: result?.data?.whatsapp_number,
      };

      setPosts((prev) =>
        prev.map((p) =>
          p.id === post.id
            ? {
                ...p,
                phone: contact.phone || p.phone,
                whatsapp: contact.whatsapp || p.whatsapp,
              }
            : p,
        ),
      );

      setVisibleContacts((prev) => ({
        ...prev,
        [post.id]: true,
      }));

      setAgreementOpen(false);
      setAgreementPost(null);
    } catch (error) {
      console.error("Apply agreement continue error:", error);
      alert("Server error. Please try again.");
    } finally {
      setAgreementLoading(false);
    }
  };

  return (
    <>
      {/* Watermark (only when logged in) */}
      {isLoggedIn && (
        <div className="tb-watermark">{`${user?.email || "User"} `}</div>
      )}
      <div className={`tuition-board-page ${blurred ? "tb-blur-on" : ""}`}>
        <div className="tb-container">
          <h1 className="text-center tuition-board-title">টিউশন বোর্ড</h1>

          {/* APPLICATION BUTTONS  */}
          <div className="application-btnss font-bold">
            <button
              className="tuition-apply-btn-N inline-flex items-center gap-2"
              onClick={() => (window.location.href = "/tuition-board")}
            >
              <i
                className="fa-solid fa-magnifying-glass text-white"
                style={{ fontSize: 10 }}
                aria-hidden="true"
              ></i>
              <span className="bg-gradient-to-r from-emerald-400 via-emerald-400 to-emerald-400 bg-clip-text text-transparent">
                Find Tuitions
              </span>
            </button>

            <button
              className="tuition-apply-btn-N inline-flex items-center gap-2"
              onClick={() => (window.location.href = "/tutor-board")}
            >
              <i
                className="fa-solid fa-user-graduate text-white"
                style={{ fontSize: 10 }}
                aria-hidden="true"
              ></i>
              <span className="bg-gradient-to-r from-emerald-400 via-emerald-400 to-emerald-400 bg-clip-text text-transparent">
                Hire Tutor
              </span>
            </button>

            <button
              className="tuition-apply-btn-N inline-flex items-center gap-2"
              onClick={() => (window.location.href = "/post-tuition")}
            >
              <i
                className="fa-solid fa-circle-plus text-white"
                style={{ fontSize: 10 }}
                aria-hidden="true"
              ></i>
              <span className="bg-gradient-to-r from-emerald-400 via-emerald-400 to-emerald-400 bg-clip-text text-transparent">
                Post Tuition
              </span>
            </button>
          </div>

          {/* <TNCounterSection /> */}

          {/* Filters */}
          <section className="tb-filters">
            {/* Search input (always visible) */}
            <div
              className="tb-filter-item tb-filter-item-search"
              style={{ position: "relative" }}
            >
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: 12,
                  top: "35%",
                  transform: "translateY(-50%)",
                  opacity: 0.65,
                  pointerEvents: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Search Icon (SVG) */}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M21 21l-4.35-4.35"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>

              <input
                type="text"
                placeholder="নাম, সাবজেক্ট, লোকেশন, আইডি দিয়ে সার্চ করো…"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                style={{
                  width: "100%",
                  paddingLeft: 40, //  room for icon
                }}
              />
            </div>

            {isTutor && (
              <div className="mt-6 w-full">
                <div className="group relative overflow-hidden rounded-2xl border border-slate-700/50 bg-gradient-to-r from-slate-900/90 to-slate-800/80 backdrop-blur-xl transition-all duration-500 hover:border-[#25d976]/50">
                  {/* Background decoration */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#25d976]/0 via-[#25d976]/5 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                  <div className="relative z-10 flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                    {/* Left: Credits info */}
                    <div className="flex items-center gap-4">
                      {/* Icon badge */}
                      <div className="relative h-12 w-12 flex-shrink-0">
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#25d976] to-emerald-500 opacity-20 blur-md" />
                        <div className="relative flex h-full w-full items-center justify-center rounded-xl border border-[#25d976]/30 bg-gradient-to-br from-[#25d976]/20 to-[#25d976]/5">
                          <svg
                            className="h-6 w-6 text-[#25d976]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* Credits and badge */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm font-medium text-slate-400">
                            Applications left:
                          </span>
                          <span className="text-xl font-bold text-white tabular-nums">
                            {wallet.credits}
                          </span>
                        </div>

                        {/* {Number(wallet?.credits || 0) > 0 &&
                          Number(wallet?.lifetime_purchased || 0) === 0 && (
                            <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-semibold text-emerald-300">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                              Free applications from us.
                            </div>
                          )} */}
                      </div>
                    </div>

                    {/* Right: CTA button */}
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/packages");
                        window.scrollTo(0, 0);
                      }}
                      className="group/btn relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-[#25d976] to-emerald-500 px-5 py-2.5 text-sm font-bold text-slate-900 shadow-lg shadow-[#25d976]/30 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#25d976]/50 focus:outline-none focus:ring-2 focus:ring-[#25d976]/50 focus:ring-offset-2 focus:ring-offset-slate-900 active:scale-100 sm:w-auto"
                    >
                      <span className="relative z-10 flex items-center justify-center gap-2 whitespace-nowrap">
                        Get Tutor Pro
                        <svg
                          className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                          />
                        </svg>
                      </span>
                      <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover/btn:translate-x-full" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/*  Filter Section */}
            <div className="baloo-da-2-tutor search-filter-section">
              <div className="search-container">
                {/* Filter toggle button */}
                <button
                  onClick={() => setShowFilters((prev) => !prev)}
                  className={`filter-toggle ${showFilters ? "active" : ""}`}
                >
                  <Filter size={18} />
                  <span>ফিল্টার</span>
                  {hasActiveFilters && <div className="filter-dot"></div>}
                </button>
              </div>

              {/* Filter Panel */}
              <div className={`filter-panel ${showFilters ? "show" : ""}`}>
                <div className="filter-header">
                  <h3>টিউশন ফিল্টার করো</h3>
                  {hasActiveFilters && (
                    <button onClick={clearFilters} className="clear-filters">
                      Clear All
                    </button>
                  )}
                </div>

                {/* Filter Categories */}
                <div className="filter-grid">
                  <div className="filter-group">
                    <label>Medium</label>
                    <Select
                      classNamePrefix="tb-select"
                      options={mediumSelectOptions}
                      value={mediumSelectOptions.find(
                        (opt) => opt.value === filters.medium,
                      )}
                      onChange={handleSelectChange("medium")}
                      isClearable={false}
                    />
                  </div>

                  <div className="filter-group">
                    <label>Level</label>
                    <Select
                      classNamePrefix="tb-select"
                      options={levelSelectOptions}
                      value={levelSelectOptions.find(
                        (opt) => opt.value === filters.level,
                      )}
                      onChange={handleSelectChange("level")}
                      isClearable={false}
                    />
                  </div>

                  <div className="filter-group">
                    <label>Subject</label>
                    <Select
                      classNamePrefix="tb-select"
                      options={subjectSelectOptions}
                      value={subjectSelectOptions.find(
                        (opt) => opt.value === filters.subject,
                      )}
                      onChange={handleSelectChange("subject")}
                      isClearable={false}
                    />
                  </div>

                  <div className="filter-group">
                    <label>Division</label>
                    <Select
                      classNamePrefix="tb-select"
                      options={divisionSelectOptions}
                      value={divisionSelectOptions.find(
                        (opt) => opt.value === filters.division,
                      )}
                      onChange={handleSelectChange("division")}
                      isClearable={false}
                    />
                  </div>

                  <div className="filter-group">
                    <label>District</label>
                    <Select
                      classNamePrefix="tb-select"
                      options={districtSelectOptions}
                      value={districtSelectOptions.find(
                        (opt) => opt.value === filters.district,
                      )}
                      onChange={handleSelectChange("district")}
                      isClearable={false}
                    />
                  </div>

                  <div className="filter-group">
                    <label>Area</label>
                    <Select
                      classNamePrefix="tb-select"
                      options={areaSelectOptions}
                      value={areaSelectOptions.find(
                        (opt) => opt.value === filters.area,
                      )}
                      onChange={handleSelectChange("area")}
                      isClearable={false}
                    />
                  </div>

                  <div className="filter-group">
                    <label>Gender</label>
                    <Select
                      classNamePrefix="tb-select"
                      options={genderSelectOptions}
                      value={genderSelectOptions.find(
                        (opt) => opt.value === filters.gender,
                      )}
                      onChange={handleSelectChange("gender")}
                      isClearable={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Content */}
          <section>
            {loading && (
              <div className="tb-state tb-state-loading">
                <div className="tb-spinner" />
                <p>Tuition posts loading…</p>
              </div>
            )}

            {!loading && error && (
              <div className="tb-state tb-state-error">
                <p>{error}</p>
              </div>
            )}

            {!loading && !error && filteredPosts.length === 0 && (
              <div className="tb-state tb-state-empty">
                <p>এই ফিল্টারে কোনো টিউশন পোস্ট পাওয়া যায়নি।</p>
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="tb-reset-button"
                >
                  সব পোস্ট দেখুন
                </button>
              </div>
            )}

            {!loading && !error && filteredPosts.length > 0 && (
              <>
                <div className="tb-grid">
                  {pagePosts.map((post) => {
                    const heading = ` ${post.name} | ${
                      post.level || "Level not set"
                    }`;

                    const locationText = post.location || "Location not set";
                    const locationShouldScroll = locationText.length > 24;

                    const cardTypeClass = (() => {
                      const t = (post.posterType || "").toLowerCase().trim();
                      if (t === "tuition media" || t === "tution media")
                        return "tb-card--media";
                      if (t === "guardian") return "tb-card--guardian";
                      // default: treat anything else as student
                      return "tb-card--student";
                    })();

                    return (
                      <article
                        key={post.id}
                        className="group relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-500/10"
                      >
                        {/* Accent glow */}
                        <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                          <div className="absolute -right-24 -top-24 h-48 w-48 rounded-full bg-emerald-400/20 blur-3xl" />
                          <div className="absolute -left-24 -bottom-24 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl" />
                        </div>

                        {/* Top bar */}
                        <div className="relative flex items-start justify-between gap-3 p-4 sm:p-5">
                          <div className="min-w-0 flex-1">
                            <h2 className="text-black max-w-full truncate text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
                              {post.name || "Student"}
                            </h2>

                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <span className="inline-flex items-center rounded-lg bg-slate-100 px-2.5 py-1 text-[13px] font-semibold text-slate-700 ring-1 ring-inset ring-slate-200">
                                {post.level || "Level not set"}
                              </span>
                              {/* Medium (more visible) */}
                              <span className="inline-flex items-center rounded-lg bg-emerald-50 px-2.5 py-1 text-[13px] font-extrabold text-[#25d976]">
                                {post.medium || "Any medium"}
                              </span>
                            </div>
                          </div>

                          {/* Poster type */}
                          {(() => {
                            const pt = (
                              post.posterType || "Student"
                            ).toLowerCase();
                            const badge = pt.includes("media")
                              ? "bg-purple-50 text-purple-700 ring-purple-200"
                              : pt.includes("guardian")
                                ? "bg-blue-50 text-blue-700 ring-blue-200"
                                : "bg-emerald-50 text-emerald-700 ring-emerald-200";

                            return (
                              <span
                                className={`text-black inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ring-1 ${badge}`}
                                title="Poster type"
                              >
                                <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                                {post.posterType || "Student"}
                              </span>
                            );
                          })()}
                        </div>

                        {/* Key info row */}
                        <div className="relative grid min-w-0 gap-2 px-4 pb-4 overflow-hidden sm:grid-cols-2 sm:px-5">
                          {/* Location */}
                          <div className="flex w-full min-w-0 max-w-full items-start gap-2.5 overflow-hidden rounded-xl border border-slate-200/70 bg-slate-50 p-3">
                            <div className="mt-0.5 grid h-8 w-8 place-items-center rounded-lg bg-emerald-500/10 text-emerald-600 ring-1 ring-inset ring-emerald-500/15">
                              <MapPin size={16} aria-hidden="true" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-black text-[13px] font-semibold uppercase tracking-wide text-slate-500">
                                Location
                              </div>

                              <div className="mt-0.5 w-full max-w-full overflow-hidden">
                                {!locationShouldScroll ? (
                                  <div className="truncate text-sm font-bold text-slate-900">
                                    {locationText}
                                  </div>
                                ) : (
                                  <div className="whitespace-nowrap text-sm font-bold text-slate-900">
                                    <div className="flex w-max items-center gap-10 will-change-transform animate-[locMarquee_10s_linear_infinite] hover:[animation-play-state:paused]">
                                      <span>{locationText}</span>
                                      <span aria-hidden="true">
                                        {locationText}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Salary */}
                          <div className="flex w-full min-w-0 max-w-full items-start gap-2.5 overflow-hidden rounded-xl border border-emerald-200/70 bg-emerald-50 p-3">
                            <div className="mt-0.5 grid h-8 w-8 place-items-center rounded-lg bg-emerald-600/10 text-emerald-700 ring-1 ring-inset ring-emerald-600/15">
                              <Banknote size={16} aria-hidden="true" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-[13px] font-semibold uppercase tracking-wide text-[#25d976]">
                                Salary
                              </div>
                              <div className="truncate text-sm font-extrabold text-[#25d976] ">
                                {post.salaryText || "Negotiable"}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Subjects */}
                        {post.subjects?.length > 0 && (
                          <div className="relative px-4 pb-4 sm:px-5">
                            <div className="mb-2 flex items-center justify-between">
                              <span className="text-black text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Subjects
                              </span>
                            </div>

                            <div className="text-black flex flex-wrap gap-1.5">
                              {post.subjects.slice(0, 6).map((sub) => (
                                <span
                                  key={sub}
                                  className="text-black inline-flex items-center rounded-lg bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700 ring-1 ring-inset"
                                >
                                  {sub}
                                </span>
                              ))}
                              {post.subjects.length > 6 && (
                                <span className="text-black inline-flex items-center rounded-lg bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 ring-1 ring-inset ring-slate-200">
                                  +{post.subjects.length - 6} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Meta chips */}
                        <div className="relative flex flex-wrap gap-2 px-4 pb-4 text-xs sm:px-5">
                          {post.genderPreference && (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-700 ring-1 ring-inset ring-slate-200">
                              <span aria-hidden="true">
                                👤 Preferred gender:
                              </span>
                              {post.genderPreference === "Any"
                                ? "Any Gender"
                                : `${post.genderPreference} tutor`}
                            </span>
                          )}

                          {post.weekdays?.length > 0 && (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-700 ring-1 ring-inset ring-slate-200">
                              <CalendarDays size={14} aria-hidden="true" />
                              {post.weekdays.slice(0, 2).join(", ")}
                              {post.weekdays.length > 2
                                ? ` +${post.weekdays.length - 2}`
                                : ""}
                            </span>
                          )}

                          {post.institutePreference &&
                            post.institutePreference !== "Any" && (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-700 ring-1 ring-inset ring-slate-200">
                                <Building size={14} aria-hidden="true" />
                                Preferred Background: {post.institutePreference}
                              </span>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="relative border-t border-slate-200/70 bg-slate-50/60 p-4 sm:p-5">
                          {/* Micro note */}
                          <div className="mb-3 flex items-start justify-between gap-3">
                            <p className="text-xs text-slate-500">
                              Direct application access for registered tutors.
                            </p>

                            <span className="hidden text-xs font-semibold text-emerald-700/80 sm:inline">
                              Quick apply
                            </span>
                          </div>

                          {/* CTA */}

                          {/* View Tuition  */}
                          <button
                            type="button"
                            className="mb-3 w-full rounded-xl border border-emerald-500 bg-white px-4 py-3 text-center font-extrabold text-emerald-600 transition-all duration-300 hover:bg-emerald-50 active:scale-100"
                            onClick={() => {
                              window.scrollTo(0, 0);
                              navigate(`/tuition/${post.id}`);
                            }}
                          >
                            View Tuition Details
                          </button>

                          {!isLoggedIn ? (
                            <button
                              type="button"
                              className="relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 px-4 py-3 text-center font-extrabold text-white shadow-lg shadow-emerald-500/25 transition-all duration-300 hover:scale-[1.01] hover:shadow-xl hover:shadow-emerald-500/30 active:scale-100"
                              onClick={() => {
                                window.scrollTo(0, 0);
                                navigate("/tutor-submission");
                              }}
                            >
                              <span className="relative z-10 flex items-center justify-center gap-2">
                                <Phone size={18} aria-hidden="true" />
                                Join as Tutor to Apply
                              </span>
                              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                            </button>
                          ) : isTutor ? (
                            <>
                              <button
                                type="button"
                                className={`relative w-full overflow-hidden rounded-xl px-4 py-3 text-center font-extrabold shadow-lg transition-all duration-300 hover:scale-[1.01] active:scale-100 ${
                                  visibleContacts[post.id]
                                    ? "bg-slate-200 text-slate-800 shadow-slate-400/20 hover:bg-slate-300"
                                    : "bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/30"
                                }`}
                                onClick={() => {
                                  const isOpen = !!visibleContacts[post.id];

                                  if (isOpen) {
                                    setVisibleContacts((prev) => ({
                                      ...prev,
                                      [post.id]: false,
                                    }));
                                    return;
                                  }

                                  openApplyAgreement(post);
                                }}
                              >
                                <span className="relative z-10 flex items-center justify-center gap-2 whitespace-nowrap">
                                  {visibleContacts[post.id] ? (
                                    "Hide contact"
                                  ) : (
                                    <>
                                      <Phone size={18} aria-hidden="true" />
                                      Apply Now
                                    </>
                                  )}
                                </span>
                                {!visibleContacts[post.id] && (
                                  <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                                )}
                              </button>

                              {/* Reveal panel */}
                              {visibleContacts[post.id] && (
                                <div className="mt-3 space-y-3 rounded-xl border border-emerald-200/60 bg-gradient-to-br from-emerald-50 to-cyan-50 p-4">
                                  <div>
                                    <p className="text-sm font-extrabold text-slate-900">
                                      Contact Directly
                                    </p>
                                    <p className="mt-1 text-xs text-slate-600">
                                      Please be polite and professional when
                                      contacting.
                                    </p>
                                  </div>

                                  {post.whatsapp &&
                                    (() => {
                                      const waPhone = normalizeBDPhone(
                                        post.whatsapp,
                                      );
                                      const postWhatsappText =
                                        buildTutorWhatsappText({
                                          tutor: tutorData,
                                          tutorProfileUrl,
                                          tuitionCode: post.name,
                                        });
                                      return waPhone ? (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleSendProfile(post)
                                          }
                                          className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-green-600 to-green-500 px-4 py-3 font-extrabold text-white shadow-lg shadow-green-500/25 transition-all duration-300 hover:scale-[1.01] hover:shadow-xl hover:shadow-green-500/30 active:scale-100"
                                        >
                                          <MessageCircle
                                            size={18}
                                            aria-hidden="true"
                                          />

                                          <span className="whitespace-nowrap">
                                            Send Profile
                                          </span>
                                        </button>
                                      ) : (
                                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm font-semibold text-red-700">
                                          WhatsApp not available
                                        </div>
                                      );
                                    })()}
                                </div>
                              )}
                            </>
                          ) : (
                            <button
                              type="button"
                              className="w-full rounded-xl bg-slate-200 px-4 py-3 text-center font-bold text-slate-800 shadow-sm transition-all duration-300 hover:bg-slate-300 active:scale-100"
                              onClick={() => navigate("/tutor-submission")}
                            >
                              Only tutors can apply — Register as a tutor
                            </button>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>

                {/* Pagination (number list style) */}
                <div className="tb-pagination">
                  <button
                    type="button"
                    className="tb-page-btn"
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    disabled={page === 1}
                    aria-label="Previous page"
                  >
                    ‹
                  </button>

                  {/* first */}
                  <button
                    type="button"
                    className={`tb-page-num ${page === 1 ? "active" : ""}`}
                    onClick={() => {
                      setPage(1);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    1
                  </button>

                  {/* left dots */}
                  {page > 3 && <span className="tb-page-dots">…</span>}

                  {/* middle window */}
                  {Array.from({ length: 3 }, (_, i) => page - 1 + i)
                    .filter((n) => n > 1 && n < totalPages)
                    .map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`tb-page-num ${page === n ? "active" : ""}`}
                        onClick={() => {
                          setPage(n);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        {n}
                      </button>
                    ))}

                  {/* right dots */}
                  {page < totalPages - 2 && (
                    <span className="tb-page-dots">…</span>
                  )}

                  {/* last */}
                  {totalPages > 1 && (
                    <button
                      type="button"
                      className={`tb-page-num ${page === totalPages ? "active" : ""}`}
                      onClick={() => {
                        setPage(totalPages);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      {totalPages}
                    </button>
                  )}

                  <button
                    type="button"
                    className="tb-page-btn"
                    onClick={() => {
                      setPage((p) => Math.min(totalPages, p + 1));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    disabled={page >= totalPages}
                    aria-label="Next page"
                  >
                    ›
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      </div>

      {agreementOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/60 sm:items-center"
          onClick={closeAgreement}
        >
          <div
            className="
        flex w-full max-w-lg flex-col
        rounded-t-3xl bg-white shadow-2xl
        sm:rounded-2xl
        max-h-[90vh]
      "
            onClick={(event) => event.stopPropagation()}
          >
            {/* Header */}
            <div className="border-b border-slate-100 px-5 pb-4 pt-6 sm:p-6">
              <h2 className="text-lg font-black text-slate-950 sm:text-xl">
                Tutor Application Agreement
              </h2>

              <p className="mt-2 text-sm font-medium leading-6 text-slate-500">
                Before applying, please read and agree to the following terms.
              </p>
            </div>

            {/* Scroll Content */}
            <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
              <div className="space-y-4">
                <div
                  className="
            rounded-2xl border border-amber-200 
            bg-amber-50 p-4
          "
                >
                  <p
                    className="
              text-sm font-bold leading-6 text-amber-800
            "
                  >
                    1. I understand that I am required to provide 2 demo classes
                    to the guardian for final confirmation.
                  </p>
                </div>

                <div
                  className="
            rounded-2xl border border-blue-200 
            bg-blue-50 p-4
          "
                >
                  <p
                    className="
              text-sm font-bold leading-6 text-blue-800
            "
                  >
                    2. Tutor Network checks tuition confirmation with the
                    guardian. If my tuition is confirmed, 50% of the first
                    month’s salary must be paid within 7 days of confirmation.
                    This is a one-time payment.
                  </p>
                </div>
              </div>
            </div>

            {/* Mobile Sticky Actions */}
            <div
              className="
        border-t border-slate-100
        bg-white
        px-5 py-4
        sm:px-6
      "
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  disabled={applyLoading}
                  onClick={closeAgreement}
                  className="
              min-h-[48px]
              rounded-xl
              border border-slate-200
              bg-white
              px-4 py-3
              text-sm font-black
              text-slate-700
              transition
              hover:bg-slate-50
              disabled:opacity-50
            "
                >
                  Disagree
                </button>

                <button
                  type="button"
                  disabled={applyLoading}
                  onClick={continueApplyAfterAgreement}
                  className="
              min-h-[48px]
              rounded-xl
              bg-gradient-to-r
              from-emerald-600
              to-emerald-500
              px-4 py-3
              text-sm font-black
              text-white
              shadow-lg
              shadow-emerald-500/25
              transition
              active:scale-[0.98]
              disabled:opacity-50
            "
                >
                  {applyLoading ? "Please wait..." : "Agree & Continue"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {profileReminderPost && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4"
          onClick={() => setProfileReminderPost(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-black text-slate-900">
              Before sending your profile
            </h2>

            <p className="mt-3 text-sm font-semibold leading-6 text-slate-600">
              Guardian-এর সাথে আগে কথা বললে আপনার Profile সহজে মনে থাকবে এবং
              আপনাকে বিবেচনা করার সম্ভাবনা বাড়বে।
            </p>

            <div className="mt-5 grid gap-3">
              <button
                type="button"
                onClick={() => {
                  const post = profileReminderPost;

                  setProfileReminderPost(null);

                  if (post.phone) {
                    window.location.href = `tel:${post.phone}`;
                  }
                }}
                className="rounded-xl bg-blue-600 px-4 py-3 font-black text-white"
              >
                Call Guardian
              </button>

              <button
                type="button"
                onClick={() => {
                  const post = profileReminderPost;

                  setProfileReminderPost(null);

                  openTutorProfileMessage(post);
                }}
                className="rounded-xl bg-slate-100 px-4 py-3 font-black text-slate-800"
              >
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}
